'use client'

import ChatPopupButton, { ChatPopupProvider } from '@/components/chat-popup'
import { CHAT_READ_CHANGED_EVENT } from '@/lib/api/atendimento/chat'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import ConfirmModal from '@/components/confirm-modal'
import {
    DetailPopupProvider,
    DetailPopupButton,
} from '@/components/detail-popup'
import { dispatchPipelineRefresh } from '@/components/sales/SalesPipelineDropdown'
import {
    createStage,
    deleteStage,
    getDealCountsByStage,
    getDeals,
    getPipeline,
    getPipelineRoutingOwner,
    moveDealStage,
    PIPELINE_ROUTING_FIELDS,
    PIPELINE_TYPE_LABELS,
    PipelineRouting,
    PipelineRoutingOwner,
    PipelineStage,
    reorderStages,
    SalesPipeline,
    updatePipeline,
    updateStage,
} from '@/lib/api/sales'
import { Deal } from '@/lib/api/sales/deals-api'
import {
    LEAD_SENTIMENT_LABELS,
    LEAD_TEMPERATURE_LABELS,
    LeadSentimentType,
    LeadTemperature,
} from '@/lib/api/sales/leads-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { SocketContext } from '@/lib/providers/socket-provider'
import { markPipelineSeen } from '@/lib/sales/pipeline-last-seen'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'
import { useAuth } from '@/store/hooks'
import {
    DragDropContext,
    Draggable,
    Droppable,
    DropResult,
} from '@hello-pangea/dnd'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { BiChevronRight } from 'react-icons/bi'
import { CgEye } from 'react-icons/cg'
import { IoSwapHorizontal } from 'react-icons/io5'
import {
    MdAccessTime,
    MdAccountTree,
    MdAdd,
    MdArrowBack,
    MdAutorenew,
    MdInfoOutline,
    MdClose,
    MdSearch,
    MdForum,
    MdDelete,
    MdDriveFileMove,
    MdEdit,
    MdFullscreen,
    MdFullscreenExit,
    MdHandshake,
    MdPerson,
    MdSave,
    MdShuffle,
    MdWarning,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import MoveDealPipelineModal from './move-deal-pipeline-modal'
import PipelineFlowsModal from './pipeline-flows-modal'
import PipelineRestrictedBadge from './pipeline-restricted-badge'
import { registerScreenController } from '@/lib/ai-user/screen-bridge'

interface StageWithDeals extends PipelineStage {
    deals: Deal[]
    totalValue: number // total real (backend sum), nao soma dos carregados
    total: number // total de deals (backend count)
    page: number // ultima pagina carregada
    loadingMore: boolean
}

const STAGE_PAGE_SIZE = 20

/** Espelha `MIN_SEARCH_LENGTH` do backend (`deal.service.ts`); mudou lá, mude aqui. */
const MIN_SEARCH_LENGTH = 3

export default function PipelineDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { socket } = useContext(SocketContext)
    // `pipelines` responde pela ESTRUTURA do funil (criar, editar e reordenar
    // etapas, movimentação automática). Mexer no card — arrastar, mudar de
    // etapa, mandar para outro funil — é `deals`, a mesma permissão que o
    // backend cobra em `PATCH sales/deals/:id/move`. Antes tudo saía de
    // `pipelines.update`: o vendedor, que não edita funil, ficava sem o botão
    // de mover, e quem tinha só `pipelines` via o botão e tomava 403 ao salvar.
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'sales',
        'pipelines'
    )
    const { canUpdate: canUpdateDeals } = useResourcePermissions(
        'sales',
        'deals'
    )
    const { canRead: canReadFlows } = useResourcePermissions(
        'sales',
        'automations'
    )
    const [showFlowsModal, setShowFlowsModal] = useState(false)

    const [loading, setLoading] = useState(true)
    const [pipeline, setPipeline] = useState<SalesPipeline | null>(null)
    const [stagesWithDeals, setStagesWithDeals] = useState<StageWithDeals[]>([])
    // Espelho para o recarregamento por socket, cujo handler guarda a closure antiga
    const stagesRef = useRef<StageWithDeals[]>([])
    stagesRef.current = stagesWithDeals
    const [allDeals, setAllDeals] = useState<Deal[]>([])

    /**
     * Busca do quadro.
     *
     * São dois estados de propósito: `searchInput` é o que a pessoa digita (a cada
     * tecla) e `search` é o que vai ao servidor, 400ms depois da última. Sem essa
     * separação, cada letra dispararia uma consulta POR ETAPA mais a contagem — num
     * funil de sete etapas, oito requisições por tecla.
     */
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')

    const [searchOpen, setSearchOpen] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400)
        return () => clearTimeout(timer)
    }, [searchInput])

    /**
     * O campo sai por PORTAL, como "Meus Alertas": o cabeçalho e o quadro criam
     * contextos de empilhamento e rolagem horizontal, e um painel `absolute` aqui
     * seria cortado pela faixa das colunas.
     *
     * Fechar com termo digitado NÃO limpa a busca: o quadro continua filtrado e a lupa
     * fica acesa, que é como a pessoa sabe que ainda há um filtro valendo.
     */
    const { anchorRef: searchAnchorRef, renderPanel: renderSearchPanel } =
        useAnchoredDropdown(searchOpen, () => setSearchOpen(false), {
            matchAnchorWidth: false,
        })

    const searchFieldRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
        if (searchOpen) searchFieldRef.current?.focus()
    }, [searchOpen])
    const [viewMode, setViewMode] = useState<'deals' | 'leads'>(() => {
        return (
            (localStorage.getItem('pipeline-view-mode') as 'deals' | 'leads') ||
            'deals'
        )
    })

    const handleViewMode = (mode: 'deals' | 'leads') => {
        setViewMode(mode)
        localStorage.setItem('pipeline-view-mode', mode)
    }

    // Stage modal
    const [showStageModal, setShowStageModal] = useState(false)
    const [editingStage, setEditingStage] = useState<PipelineStage | null>(null)
    const [stageName, setStageName] = useState('')
    const [stageColor, setStageColor] = useState('#3B82F6')
    const [stageProbability, setStageProbability] = useState(0)
    const [stageRottingDays, setStageRottingDays] = useState('')
    const [stageIsWon, setStageIsWon] = useState(false)
    const [stageIsLost, setStageIsLost] = useState(false)
    const [stageHiddenFromAi, setStageHiddenFromAi] = useState(false)
    const [savingStage, setSavingStage] = useState(false)

    // Delete stage
    const [showDeleteStageModal, setShowDeleteStageModal] = useState(false)
    const [stageToDelete, setStageToDelete] = useState<PipelineStage | null>(
        null
    )
    const [deletingStage, setDeletingStage] = useState(false)

    // Movimentação automática (docs/sales-stage-routing.md)
    const EMPTY_ROUTING: PipelineRouting = {
        appointmentStageId: null,
        budgetStageId: null,
        budgetApprovedStageId: null,
        paidStageId: null,
    }
    const [showRoutingModal, setShowRoutingModal] = useState(false)
    const [routingForm, setRoutingForm] =
        useState<PipelineRouting>(EMPTY_ROUTING)
    const [routingOwner, setRoutingOwner] =
        useState<PipelineRoutingOwner['paid']>(null)
    const [savingRouting, setSavingRouting] = useState(false)

    // Fullscreen + drag-to-pan
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [isDndDragging, setIsDndDragging] = useState(false)
    // `dealIds`: no modo Leads o card representa o lead e move todas as oportunidades
    // dele naquela etapa (normalmente uma só).
    const [moveDropdown, setMoveDropdown] = useState<{
        dealIds: number[]
        dealTitle: string
        stageId: number
        x: number
        y: number
    } | null>(null)
    // Mover para outro funil: aberto a partir do mesmo menu do card
    const [movePipelineDeal, setMovePipelineDeal] = useState<{
        ids: number[]
        title: string
    } | null>(null)
    const boardRef = useRef<HTMLDivElement>(null)
    const isPanning = useRef(false)
    const panStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 })

    const handleBoardPointerDown = (e: React.PointerEvent) => {
        if (isDndDragging) return
        const board = boardRef.current
        if (!board) return
        if (
            (e.target as HTMLElement).closest(
                '.stage-column, .deal-card, button, [role="button"]'
            )
        )
            return
        isPanning.current = true
        panStart.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: board.scrollLeft,
            scrollTop: board.scrollTop,
        }
        board.style.cursor = 'grabbing'
        board.setPointerCapture(e.pointerId)
        e.preventDefault()
    }

    const handleBoardPointerMove = (e: React.PointerEvent) => {
        if (isDndDragging) return
        if (!isPanning.current || !boardRef.current) return
        const dx = e.clientX - panStart.current.x
        const dy = e.clientY - panStart.current.y
        boardRef.current.scrollLeft = panStart.current.scrollLeft - dx
        boardRef.current.scrollTop = panStart.current.scrollTop - dy
    }

    const handleBoardPointerUp = (e: React.PointerEvent) => {
        if (!isPanning.current) return
        isPanning.current = false
        if (boardRef.current) {
            boardRef.current.style.cursor = ''
            boardRef.current.releasePointerCapture(e.pointerId)
        }
    }

    // Minimap
    const [minimapViewport, setMinimapViewport] = useState({
        left: 0,
        width: 100,
    })
    const minimapDragging = useRef(false)

    const updateMinimap = useCallback(() => {
        const board = boardRef.current
        if (!board) return
        const scrollWidth = board.scrollWidth || 1
        const clientWidth = board.clientWidth || 1
        const left = (board.scrollLeft / scrollWidth) * 100
        const width = (clientWidth / scrollWidth) * 100
        setMinimapViewport({ left, width: Math.min(width, 100) })
    }, [])

    useEffect(() => {
        const board = boardRef.current
        if (!board) return
        board.addEventListener('scroll', updateMinimap)
        const resizeObserver = new ResizeObserver(updateMinimap)
        resizeObserver.observe(board)
        updateMinimap()
        return () => {
            board.removeEventListener('scroll', updateMinimap)
            resizeObserver.disconnect()
        }
    }, [updateMinimap, stagesWithDeals])

    const minimapGrabOffset = useRef(0)

    const handleMinimapPointerDown = (e: React.PointerEvent) => {
        const board = boardRef.current
        const minimap = e.currentTarget as HTMLElement
        if (!board || !minimap) return

        const rect = minimap.getBoundingClientRect()
        const clickX = e.clientX - rect.left
        const clickRatio = clickX / rect.width
        const viewportLeftRatio = minimapViewport.left / 100
        const viewportWidthRatio = minimapViewport.width / 100

        if (
            clickRatio >= viewportLeftRatio &&
            clickRatio <= viewportLeftRatio + viewportWidthRatio
        ) {
            minimapGrabOffset.current = clickRatio - viewportLeftRatio
        } else {
            minimapGrabOffset.current = viewportWidthRatio / 2
            const targetRatio = clickRatio - minimapGrabOffset.current
            board.scrollLeft = targetRatio * board.scrollWidth
        }

        minimapDragging.current = true
        minimap.setPointerCapture(e.pointerId)
    }

    const handleMinimapPointerMove = (e: React.PointerEvent) => {
        if (!minimapDragging.current) return
        const board = boardRef.current
        const minimap = e.currentTarget as HTMLElement
        if (!board || !minimap) return
        const rect = minimap.getBoundingClientRect()
        const x = e.clientX - rect.left
        const ratio = x / rect.width
        const targetRatio = ratio - minimapGrabOffset.current
        board.scrollLeft = Math.max(
            0,
            Math.min(
                targetRatio * board.scrollWidth,
                board.scrollWidth - board.clientWidth
            )
        )
    }

    const handleMinimapPointerUp = (e: React.PointerEvent) => {
        minimapDragging.current = false
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    }

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false)
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isFullscreen])

    const pipelineId = id ? parseInt(id) : null

    /** Funil já carregado nesta tela — trocar a busca não deve devolver o esqueleto. */
    const loadedPipelineRef = useRef<number | null>(null)

    useEffect(() => {
        if (pipelineId && user?.companyId) {
            // Refazer a busca é recarga SILENCIOSA: o quadro continua à vista enquanto
            // os resultados chegam. Só a primeira carga (ou outro funil) mostra o
            // esqueleto.
            loadData({ silent: loadedPipelineRef.current === pipelineId })
            loadedPipelineRef.current = pipelineId
        }
    }, [pipelineId, user?.companyId, search])

    /** `silent`: atualização por socket, sem trocar o quadro pelo esqueleto de carregamento. */
    const loadData = async ({ silent = false }: { silent?: boolean } = {}) => {
        if (!pipelineId) return
        if (!silent) setLoading(true)
        try {
            const pipelineRes = await getPipeline(pipelineId)

            if (!pipelineRes.success || !pipelineRes.result) {
                toast.error('Erro ao carregar funil')
                return
            }
            setPipeline(pipelineRes.result)

            const stages = [...(pipelineRes.result.stages || [])].sort(
                (a, b) => a.sortOrder - b.sortOrder
            )

            // Carrega counts + primeira pagina de cada stage em paralelo.
            const searchParam = search || undefined
            const countsRes = await getDealCountsByStage({
                pipelineId,
                search: searchParam,
            })
            const counts = countsRes.success ? countsRes.result || {} : {}

            // No recarregamento silencioso, cada etapa volta com as páginas que o usuário
            // já tinha aberto em "Carregar mais" — antes tudo voltava à primeira página.
            const loadedPages = new Map(
                silent
                    ? stagesRef.current.map(
                          s => [s.id, Math.max(1, s.page)] as const
                      )
                    : []
            )
            const pageResults = await Promise.all(
                stages.map(async stage => {
                    const pages = loadedPages.get(stage.id) ?? 1
                    const results = await Promise.all(
                        Array.from({ length: pages }, (_, p) =>
                            getDeals({
                                pipelineId,
                                stageId: stage.id,
                                search: searchParam,
                                page: p + 1,
                                limit: STAGE_PAGE_SIZE,
                            })
                        )
                    )
                    const items = results.flatMap(r =>
                        r.success && r.result ? r.result.items : []
                    )
                    // Sem duplicar card que mudou de página entre uma consulta e outra
                    const unique = Array.from(
                        new Map(items.map(d => [d.id, d])).values()
                    )
                    return { items: unique, pages }
                })
            )

            const mapped: StageWithDeals[] = stages.map((stage, i) => {
                const { items, pages } = pageResults[i]
                const stats = counts[stage.id] || { count: 0, totalValue: 0 }
                return {
                    ...stage,
                    deals: items,
                    total: stats.count,
                    totalValue: stats.totalValue,
                    page: pages,
                    loadingMore: false,
                }
            })
            setStagesWithDeals(mapped)
            setAllDeals(mapped.flatMap(s => s.deals))
            // Quem está com o funil aberto já viu o que entrou: zera o badge do dropdown
            if (user?.id) markPipelineSeen(user.id, pipelineId)
        } catch {
            toast.error('Erro ao carregar dados do funil')
        } finally {
            setLoading(false)
        }
    }

    const loadMoreForStage = async (stageId: number) => {
        if (!pipelineId) return
        const stageIdx = stagesWithDeals.findIndex(s => s.id === stageId)
        if (stageIdx < 0) return
        const current = stagesWithDeals[stageIdx]
        if (current.loadingMore) return
        if (current.deals.length >= current.total) return

        setStagesWithDeals(prev =>
            prev.map(s => (s.id === stageId ? { ...s, loadingMore: true } : s))
        )

        try {
            const nextPage = current.page + 1
            const res = await getDeals({
                pipelineId,
                stageId,
                // "Carregar mais" tem de respeitar a busca, senão a etapa filtrada
                // volta a encher de cartão que não casa com o termo.
                search: search || undefined,
                page: nextPage,
                limit: STAGE_PAGE_SIZE,
            })
            if (res.success && res.result) {
                const newItems = res.result.items
                setStagesWithDeals(prev =>
                    prev.map(s =>
                        s.id === stageId
                            ? {
                                  ...s,
                                  deals: [...s.deals, ...newItems],
                                  page: nextPage,
                                  loadingMore: false,
                              }
                            : s
                    )
                )
                setAllDeals(prev => [...prev, ...newItems])
            } else {
                setStagesWithDeals(prev =>
                    prev.map(s =>
                        s.id === stageId ? { ...s, loadingMore: false } : s
                    )
                )
                toast.error(res.error || 'Erro ao carregar mais oportunidades')
            }
        } catch {
            setStagesWithDeals(prev =>
                prev.map(s =>
                    s.id === stageId ? { ...s, loadingMore: false } : s
                )
            )
            toast.error('Erro ao carregar mais oportunidades')
        }
    }

    // Atualização em tempo real via WebSocket
    // O funil é da ORGANIZAÇÃO: o canal também. O backend ainda publica no canal por
    // empresa, mas ele não vê a oportunidade da triagem, que não tem filial.
    useEffect(() => {
        if (!socket || !user?.organizationId) return
        const event = `deal-updated-org-${user.organizationId}`
        // O canal é da organização inteira: IA e fluxos mexem em várias oportunidades
        // em sequência. Junta a rajada num recarregamento só, sem piscar o quadro.
        let timer: ReturnType<typeof setTimeout> | null = null
        const handler = () => {
            if (timer) clearTimeout(timer)
            timer = setTimeout(() => loadData({ silent: true }), 800)
        }
        socket.on(event, handler)
        // A leitura da conversa é por pessoa e não passa pelo socket: quem lê pelo dock
        // sobre o funil vê o card apagar por este evento local.
        window.addEventListener(CHAT_READ_CHANGED_EVENT, handler)
        return () => {
            if (timer) clearTimeout(timer)
            socket.off(event, handler)
            window.removeEventListener(CHAT_READ_CHANGED_EVENT, handler)
        }
    }, [socket, user?.organizationId, pipelineId])

    const formatBRL = (cents: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(cents / 100)
    }

    const totalPipelineValue = stagesWithDeals.reduce(
        (sum, s) => sum + s.totalValue,
        0
    )
    const totalDeals = stagesWithDeals.reduce((sum, s) => sum + s.total, 0)

    // Drag and drop
    /**
     * Controlador deste quadro para o Modo IA (`lib/ai-user/screen-bridge.ts`): o
     * usuário de IA move o card pela MESMA rota do arrastar e a tela recarrega à
     * vista do supervisor. A ref evita registrar de novo a cada render.
     */
    const loadDataRef = useRef(loadData)
    loadDataRef.current = loadData
    useEffect(() => {
        if (!pipelineId) return
        return registerScreenController('pipeline', {
            pipelineId,
            moveDeal: async (dealId, stageId) => {
                try {
                    const response = await moveDealStage(dealId, { stageId })
                    if (!response.success) {
                        toast.error(response.error || 'Erro ao mover oportunidade')
                        return false
                    }
                    return true
                } catch {
                    toast.error('Erro ao mover oportunidade')
                    return false
                } finally {
                    await loadDataRef.current({ silent: true })
                }
            },
        })
    }, [pipelineId])

    const onDragEnd = useCallback(
        async (result: DropResult) => {
            const { source, destination, draggableId, type } = result

            if (!destination) return

            // Stage reorder
            if (type === 'STAGE') {
                const reordered = Array.from(stagesWithDeals)
                const [moved] = reordered.splice(source.index, 1)
                reordered.splice(destination.index, 0, moved)

                setStagesWithDeals(reordered)

                try {
                    await reorderStages(
                        pipelineId!,
                        reordered.map(s => s.id)
                    )
                } catch {
                    toast.error('Erro ao reordenar etapas')
                    loadData()
                }
                return
            }

            // Deal move between stages
            if (
                source.droppableId === destination.droppableId &&
                source.index === destination.index
            ) {
                return
            }

            const sourceStageId = parseInt(source.droppableId)
            const destStageId = parseInt(destination.droppableId)
            const dealId = parseInt(draggableId.replace('deal-', ''))

            // Optimistic update — atualiza deals, total e totalValue de ambas as stages.
            const movedDeal = stagesWithDeals
                .find(s => s.id === sourceStageId)
                ?.deals.find(d => d.id === dealId)
            const movedValue = movedDeal?.totalValue || 0

            const updated = stagesWithDeals.map(stage => {
                if (stage.id === sourceStageId) {
                    const deals = stage.deals.filter(d => d.id !== dealId)
                    return {
                        ...stage,
                        deals,
                        total: Math.max(0, stage.total - 1),
                        totalValue: Math.max(0, stage.totalValue - movedValue),
                    }
                }
                if (stage.id === destStageId && movedDeal) {
                    const deals = [...stage.deals]
                    deals.splice(destination.index, 0, {
                        ...movedDeal,
                        stageId: destStageId,
                        stageEnteredAt: new Date().toISOString(),
                    })
                    return {
                        ...stage,
                        deals,
                        total: stage.total + 1,
                        totalValue: stage.totalValue + movedValue,
                    }
                }
                return stage
            })
            setStagesWithDeals(updated)

            try {
                const response = await moveDealStage(dealId, {
                    stageId: destStageId,
                })
                if (!response.success) {
                    toast.error(response.error || 'Erro ao mover oportunidade')
                    loadData()
                }
            } catch {
                toast.error('Erro ao mover oportunidade')
                loadData()
            }
        },
        [stagesWithDeals, pipelineId]
    )

    // Stage CRUD
    const openCreateStage = () => {
        setEditingStage(null)
        setStageName('')
        setStageColor('#3B82F6')
        setStageProbability(0)
        setStageRottingDays('')
        setStageIsWon(false)
        setStageIsLost(false)
        setStageHiddenFromAi(false)
        setShowStageModal(true)
    }

    const openEditStage = (stage: PipelineStage) => {
        setEditingStage(stage)
        setStageName(stage.name)
        setStageColor(stage.color || '#3B82F6')
        setStageProbability(stage.probability)
        setStageRottingDays(stage.rottingDays?.toString() || '')
        setStageIsWon(stage.isWonStage)
        setStageIsLost(stage.isLostStage)
        setStageHiddenFromAi(!!stage.hiddenFromAi)
        setShowStageModal(true)
    }

    const openRoutingModal = async () => {
        if (!pipeline || !pipelineId) return
        setRoutingForm({
            appointmentStageId: pipeline.appointmentStageId ?? null,
            budgetStageId: pipeline.budgetStageId ?? null,
            budgetApprovedStageId: pipeline.budgetApprovedStageId ?? null,
            paidStageId: pipeline.paidStageId ?? null,
        })
        setRoutingOwner(null)
        setShowRoutingModal(true)
        const ownerRes = await getPipelineRoutingOwner(pipelineId)
        if (ownerRes.success && ownerRes.result)
            setRoutingOwner(ownerRes.result.paid)
    }

    const handleSaveRouting = async () => {
        if (!pipelineId) return
        setSavingRouting(true)
        try {
            const response = await updatePipeline(pipelineId, routingForm)
            if (response.success) {
                toast.success('Movimentação automática salva')
                dispatchPipelineRefresh()
                setShowRoutingModal(false)
                loadData()
            } else {
                toast.error(
                    response.error || 'Erro ao salvar a movimentação automática'
                )
            }
        } catch {
            toast.error('Erro ao salvar a movimentação automática')
        } finally {
            setSavingRouting(false)
        }
    }

    const handleSaveStage = async () => {
        if (!stageName.trim() || !pipelineId) return

        setSavingStage(true)
        try {
            const data = {
                name: stageName.trim(),
                color: stageColor,
                probability: stageProbability,
                rottingDays: stageRottingDays
                    ? parseInt(stageRottingDays)
                    : undefined,
                isWonStage: stageIsWon,
                isLostStage: stageIsLost,
                hiddenFromAi: stageHiddenFromAi,
            }

            let response
            if (editingStage) {
                response = await updateStage(pipelineId, editingStage.id, data)
            } else {
                response = await createStage(pipelineId, {
                    ...data,
                    sortOrder: stagesWithDeals.length,
                })
            }

            if (response.success) {
                toast.success(
                    editingStage ? 'Etapa atualizada' : 'Etapa criada'
                )
                dispatchPipelineRefresh()
                setShowStageModal(false)
                loadData()
            } else {
                toast.error(response.error || 'Erro ao salvar etapa')
            }
        } catch {
            toast.error('Erro ao salvar etapa')
        } finally {
            setSavingStage(false)
        }
    }

    const handleDeleteStage = (stage: PipelineStage) => {
        setStageToDelete(stage)
        setShowDeleteStageModal(true)
    }

    const confirmDeleteStage = async () => {
        if (!stageToDelete || !pipelineId) return
        setDeletingStage(true)
        try {
            const response = await deleteStage(pipelineId, stageToDelete.id)
            if (response.success) {
                toast.success('Etapa removida')
                dispatchPipelineRefresh()
                setShowDeleteStageModal(false)
                setStageToDelete(null)
                loadData()
            } else {
                toast.error(response.error || 'Erro ao remover etapa')
            }
        } catch {
            toast.error('Erro ao remover etapa')
        } finally {
            setDeletingStage(false)
        }
    }

    const getDaysInStage = (deal: Deal) => {
        if (!deal.stageEnteredAt) return 0
        const entered = new Date(deal.stageEnteredAt)
        const now = new Date()
        return Math.floor(
            (now.getTime() - entered.getTime()) / (1000 * 60 * 60 * 24)
        )
    }

    const isRotting = (deal: Deal, stage: StageWithDeals) => {
        if (!stage.rottingDays) return false
        return getDaysInStage(deal) > stage.rottingDays
    }

    const getStageHeaderColor = (stage: StageWithDeals) => {
        if (stage.isWonStage) return 'border-success bg-success/5'
        if (stage.isLostStage) return 'border-error bg-error/5'
        return 'border-base-300'
    }

    const getStageHeaderBackground = (stage: StageWithDeals) => {
        if (stage.isWonStage) return 'bg-success/15 border-success/30'
        if (stage.isLostStage) return 'bg-error/15 border-error/30'
        return 'bg-base-200 border-base-300'
    }

    const getStageIndicatorColor = (stage: StageWithDeals) => {
        if (stage.isWonStage) return 'bg-success'
        if (stage.isLostStage) return 'bg-error'
        return ''
    }

    const getUserInitials = (name?: string) => {
        if (!name) return '?'
        return name
            .split(' ')
            .slice(0, 2)
            .map(n => n[0])
            .join('')
            .toUpperCase()
    }

    // Ações de configuração do funil: num menu só, para o cabeçalho caber em monitor menor
    const headerActions: ActionMenuItem[] = [
        ...(canCreate
            ? [
                  {
                      key: 'new-stage',
                      label: 'Nova etapa',
                      icon: <MdAdd />,
                      onClick: openCreateStage,
                  },
              ]
            : []),
        ...(canReadFlows
            ? [
                  {
                      key: 'flows',
                      label: 'Fluxos',
                      icon: <MdAccountTree />,
                      onClick: () => setShowFlowsModal(true),
                      title: 'Quais fluxos disparam em cada etapa',
                  },
              ]
            : []),
        ...(canUpdate
            ? [
                  {
                      key: 'routing',
                      label: 'Movimentação automática',
                      icon: <MdAutorenew />,
                      onClick: openRoutingModal,
                      title: 'Para qual etapa o card vai em cada evento do sistema',
                  },
              ]
            : []),
    ]

    if (loading) {
        return (
            <div className="flex flex-col gap-6 p-6">
                <div className="flex items-center gap-3">
                    <div className="skeleton w-8 h-8 rounded" />
                    <div>
                        <div className="skeleton h-7 w-64 mb-1" />
                        <div className="skeleton h-4 w-40" />
                    </div>
                </div>
                <div className="flex gap-4 overflow-hidden">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex-shrink-0 w-72">
                            <div className="skeleton h-12 w-full mb-2 rounded-lg" />
                            <div className="space-y-2">
                                <div className="skeleton h-24 w-full rounded-lg" />
                                <div className="skeleton h-24 w-full rounded-lg" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!pipeline) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 p-6">
                <p className="text-base-content/60">Funil não encontrado</p>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate('/sales/pipelines')}
                >
                    Voltar para funis
                </button>
            </div>
        )
    }

    return (
        <ChatPopupProvider>
            <DetailPopupProvider>
                <div
                    className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-[999] bg-base-200' : 'h-full'}`}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-base-300/50 bg-base-100 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() =>
                                    isFullscreen
                                        ? setIsFullscreen(false)
                                        : navigate('/sales/pipelines')
                                }
                            >
                                <MdArrowBack className="text-lg" />
                            </button>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl font-bold text-base-content">
                                        {pipeline.name}
                                    </h1>
                                    <span
                                        className={`badge badge-sm ${
                                            pipeline.type === 'ACQUISITION'
                                                ? 'badge-secondary'
                                                : pipeline.type === 'RETENTION'
                                                  ? 'badge-secondary'
                                                  : pipeline.type === 'UPSELL'
                                                    ? 'badge-accent'
                                                    : 'badge-ghost'
                                        }`}
                                    >
                                        {PIPELINE_TYPE_LABELS[pipeline.type]}
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-base-content/60">
                                    <span>{totalDeals} negócios</span>
                                    <span className="font-medium text-secondary">
                                        {formatBRL(totalPipelineValue)}
                                    </span>
                                    {pipeline.owner && (
                                        <span
                                            className="flex items-center gap-1 text-xs"
                                            title="Dona do funil"
                                        >
                                            <MdPerson className="text-sm" />
                                            {pipeline.owner.name}
                                        </span>
                                    )}
                                    <PipelineRestrictedBadge
                                        pipeline={pipeline}
                                    />
                                    {pipeline.distributionGroup && (
                                        <span
                                            className="badge badge-sm badge-ghost gap-1"
                                            title="Grupo de distribuição"
                                        >
                                            <MdShuffle className="text-xs" />
                                            {pipeline.distributionGroup.name}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Busca do quadro: só a lupa fica no cabeçalho, e o campo sai
                            por cima ao abrir. Um input fixo aqui espreme o nome do funil,
                            os badges e o alternador — e passa 99% do tempo vazio.

                            O filtro é do SERVIDOR, e não um recorte do que já está na
                            tela: cada etapa carrega 20 cartões por vez, e filtrar no
                            cliente acharia só dentro desses 20 — justamente o contato
                            antigo, no fim da etapa, é o que ninguém encontra rolando. */}
                            <div ref={searchAnchorRef} className="relative">
                                <button
                                    type="button"
                                    className={`btn btn-sm btn-square ${search ? 'btn-secondary' : 'btn-ghost'}`}
                                    onClick={() =>
                                        setSearchOpen(value => !value)
                                    }
                                    aria-label="Buscar no funil"
                                    aria-expanded={searchOpen}
                                    title="Buscar no funil"
                                >
                                    <MdSearch className="text-lg" />
                                </button>
                            </div>
                            {renderSearchPanel(
                                <div className="p-2 w-72">
                                    <label className="input input-sm input-bordered flex items-center gap-2">
                                        <MdSearch className="text-base text-base-content/50 flex-shrink-0" />
                                        <input
                                            ref={searchFieldRef}
                                            type="text"
                                            className="grow min-w-0"
                                            placeholder="Contato, oportunidade ou termo da conversa"
                                            value={searchInput}
                                            onChange={event =>
                                                setSearchInput(
                                                    event.target.value
                                                )
                                            }
                                            // Esc limpa antes de fechar: fechar com o termo
                                            // ainda aplicado deixaria o quadro filtrado sem
                                            // nada à vista explicando por quê.
                                            onKeyDown={event => {
                                                if (event.key !== 'Escape')
                                                    return
                                                if (searchInput)
                                                    setSearchInput('')
                                                else setSearchOpen(false)
                                            }}
                                            aria-label="Buscar no funil"
                                        />
                                        {searchInput && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs btn-circle flex-shrink-0"
                                                onClick={() =>
                                                    setSearchInput('')
                                                }
                                                title="Limpar a busca"
                                                aria-label="Limpar a busca"
                                            >
                                                <MdClose className="text-sm" />
                                            </button>
                                        )}
                                    </label>
                                    {/* Sem este aviso, quem digita uma ou duas letras vê o
                                    quadro parado e conclui que a busca não funciona. */}
                                    {searchInput.trim().length > 0 &&
                                        searchInput.trim().length <
                                            MIN_SEARCH_LENGTH && (
                                            <p className="mt-1 px-1 text-xs text-base-content/60">
                                                Digite ao menos{' '}
                                                {MIN_SEARCH_LENGTH} letras.
                                            </p>
                                        )}
                                </div>
                            )}
                            {headerActions.length > 0 && (
                                <ActionsMenu items={headerActions} />
                            )}
                            <div className="flex gap-1">
                                <button
                                    className={`btn btn-sm ${viewMode === 'deals' ? 'btn-secondary' : 'btn-ghost'}`}
                                    onClick={() => handleViewMode('deals')}
                                >
                                    <MdHandshake size={16} />
                                    <span className="hidden sm:inline text-xs">
                                        Oportunidades
                                    </span>
                                </button>
                                <button
                                    className={`btn btn-sm ${viewMode === 'leads' ? 'btn-secondary' : 'btn-ghost'}`}
                                    onClick={() => handleViewMode('leads')}
                                >
                                    <MdPerson size={16} />
                                    <span className="hidden sm:inline text-xs">
                                        Leads
                                    </span>
                                </button>
                            </div>
                            <button
                                className="btn btn-sm btn-ghost btn-square"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                aria-label={
                                    isFullscreen
                                        ? 'Sair do modo expandido'
                                        : 'Modo expandido'
                                }
                                title={
                                    isFullscreen
                                        ? 'Sair do modo expandido'
                                        : 'Modo expandido'
                                }
                            >
                                {isFullscreen ? (
                                    <MdFullscreenExit className="text-lg" />
                                ) : (
                                    <MdFullscreen className="text-lg" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Minimap */}
                    {stagesWithDeals.length > 0 &&
                        minimapViewport.width < 100 && (
                            <div className="flex-shrink-0 px-6 py-2 bg-[#fafafa] border-b border-base-300/30">
                                <div
                                    className="relative h-7 rounded-md overflow-hidden select-none touch-none"
                                    style={{
                                        backgroundColor: 'oklch(var(--b2))',
                                        cursor: 'crosshair',
                                    }}
                                    onPointerDown={handleMinimapPointerDown}
                                    onPointerMove={handleMinimapPointerMove}
                                    onPointerUp={handleMinimapPointerUp}
                                >
                                    <div className="absolute inset-0 flex items-center gap-0.5 px-1 pointer-events-none">
                                        {stagesWithDeals.map(stage => (
                                            <div
                                                key={stage.id}
                                                className="h-4 rounded-sm flex-1 flex items-center justify-center"
                                                style={{
                                                    backgroundColor:
                                                        stage.isWonStage
                                                            ? 'oklch(0.65 0.15 150 / 0.4)'
                                                            : stage.isLostStage
                                                              ? 'oklch(0.65 0.15 25 / 0.4)'
                                                              : stage.color
                                                                ? `${stage.color}40`
                                                                : 'oklch(var(--b3))',
                                                }}
                                            >
                                                <span className="text-[7px] font-medium text-base-content/40 truncate px-0.5">
                                                    {stage.name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Área fora da viewport (escurecida) */}
                                    <div
                                        className="absolute top-0 bottom-0 left-0 rounded-l-md pointer-events-none cursor-pointer"
                                        style={{
                                            width: `${minimapViewport.left}%`,
                                            backgroundColor: 'rgba(0,0,0,0.25)',
                                        }}
                                    />
                                    <div
                                        className="absolute top-0 bottom-0 right-0 rounded-r-md pointer-events-none cursor-pointer"
                                        style={{
                                            width: `${100 - minimapViewport.left - minimapViewport.width}%`,
                                            backgroundColor: 'rgba(0,0,0,0.25)',
                                        }}
                                    />
                                    {/* Viewport indicator — grab cursor zone */}
                                    <div
                                        className="absolute top-0 bottom-0 border-2 border-secondary/80 rounded-sm"
                                        style={{
                                            left: `${minimapViewport.left}%`,
                                            width: `${minimapViewport.width}%`,
                                            cursor: 'grab',
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                    {/* Kanban Board */}
                    <div
                        ref={boardRef}
                        data-pipeline-board
                        className={`flex-1 overflow-x-auto p-6 ${isDndDragging ? '' : 'select-none'}`}
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            backgroundImage:
                                'radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)',
                            backgroundSize: '20px 20px',
                            backgroundColor: '#fafafa',
                            minHeight: isFullscreen
                                ? 'calc(100vh - 120px)'
                                : '500px',
                            cursor: isDndDragging ? 'grabbing' : 'grab',
                        }}
                        onPointerDown={handleBoardPointerDown}
                        onPointerMove={handleBoardPointerMove}
                        onPointerUp={handleBoardPointerUp}
                    >
                        <style>{`
                    [data-pipeline-board]::-webkit-scrollbar { display: none; }
                `}</style>
                        <DragDropContext
                            onDragStart={() => setIsDndDragging(true)}
                            onDragEnd={result => {
                                setIsDndDragging(false)
                                onDragEnd(result)
                            }}
                        >
                            <Droppable
                                droppableId="pipeline"
                                direction="horizontal"
                                type="STAGE"
                            >
                                {provided => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="flex gap-5 items-start"
                                    >
                                        {stagesWithDeals.map(
                                            (stage, stageIndex) => (
                                                <Draggable
                                                    key={`stage-${stage.id}`}
                                                    draggableId={`stage-${stage.id}`}
                                                    index={stageIndex}
                                                    isDragDisabled={!canUpdate}
                                                >
                                                    {(
                                                        dragProvided,
                                                        dragSnapshot
                                                    ) => (
                                                        <div
                                                            ref={
                                                                dragProvided.innerRef
                                                            }
                                                            {...dragProvided.draggableProps}
                                                            className={`stage-column flex flex-col flex-shrink-0 w-72 rounded-xl border-2 bg-base-100 ${getStageHeaderColor(stage)} ${
                                                                dragSnapshot.isDragging
                                                                    ? 'shadow-2xl rotate-1'
                                                                    : ''
                                                            }`}
                                                            style={
                                                                dragProvided
                                                                    .draggableProps
                                                                    .style
                                                            }
                                                        >
                                                            {/* Stage Header */}
                                                            {/* Fundo próprio, separado dos cards: o total da etapa
                                                        era lido como valor do primeiro card da coluna. */}
                                                            <div
                                                                {...dragProvided.dragHandleProps}
                                                                className={`p-3 flex-shrink-0 rounded-t-[10px] border-b ${getStageHeaderBackground(stage)}`}
                                                                style={
                                                                    !stage.isWonStage &&
                                                                    !stage.isLostStage &&
                                                                    stage.color
                                                                        ? {
                                                                              backgroundImage: `linear-gradient(${stage.color}1F, ${stage.color}1F)`,
                                                                          }
                                                                        : undefined
                                                                }
                                                            >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <div
                                                                            className={`w-3 h-3 rounded-full ${getStageIndicatorColor(stage)}`}
                                                                            style={
                                                                                !stage.isWonStage &&
                                                                                !stage.isLostStage &&
                                                                                stage.color
                                                                                    ? {
                                                                                          backgroundColor:
                                                                                              stage.color,
                                                                                      }
                                                                                    : undefined
                                                                            }
                                                                        />
                                                                        <h3 className="font-bold text-sm text-base-content truncate max-w-[140px]">
                                                                            {
                                                                                stage.name
                                                                            }
                                                                        </h3>
                                                                        <span className="badge badge-xs badge-ghost">
                                                                            {
                                                                                stage.total
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-0.5">
                                                                        {canUpdate && (
                                                                            <button
                                                                                className="btn btn-ghost btn-xs px-1"
                                                                                onClick={() =>
                                                                                    openEditStage(
                                                                                        stage
                                                                                    )
                                                                                }
                                                                                title="Editar etapa"
                                                                            >
                                                                                <MdEdit className="text-xs" />
                                                                            </button>
                                                                        )}
                                                                        {canDelete &&
                                                                            stage.total ===
                                                                                0 && (
                                                                                <button
                                                                                    className="btn btn-ghost btn-xs px-1 text-error"
                                                                                    onClick={() =>
                                                                                        handleDeleteStage(
                                                                                            stage
                                                                                        )
                                                                                    }
                                                                                    title="Remover etapa"
                                                                                >
                                                                                    <MdDelete className="text-xs" />
                                                                                </button>
                                                                            )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center justify-between text-xs text-base-content/50">
                                                                    <span>
                                                                        {
                                                                            stage.probability
                                                                        }
                                                                        %
                                                                        probabilidade
                                                                    </span>
                                                                    <span className="font-medium text-base-content/70">
                                                                        {formatBRL(
                                                                            stage.totalValue
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                {stage.rottingDays && (
                                                                    <div className="flex items-center gap-1 mt-1 text-xs text-warning">
                                                                        <MdAccessTime className="text-xs" />
                                                                        <span>
                                                                            {
                                                                                stage.rottingDays
                                                                            }
                                                                            d
                                                                            para
                                                                            estagnação
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {stage.hiddenFromAi && (
                                                                    <div className="mt-1 text-xs text-base-content/50">
                                                                        Oculta
                                                                        para a
                                                                        IA
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {viewMode ===
                                                            'leads' ? (
                                                                /* ===== LEADS VIEW ===== */
                                                                <div className="flex-1 overflow-y-auto p-2 pt-0 space-y-2 min-h-[60px]">
                                                                    {(() => {
                                                                        // Extrair leads únicos dos deals desta etapa
                                                                        const leadsMap =
                                                                            new Map<
                                                                                number,
                                                                                any
                                                                            >()
                                                                        stage.deals.forEach(
                                                                            deal => {
                                                                                if (
                                                                                    deal.lead &&
                                                                                    !leadsMap.has(
                                                                                        deal
                                                                                            .lead
                                                                                            .id
                                                                                    )
                                                                                ) {
                                                                                    leadsMap.set(
                                                                                        deal
                                                                                            .lead
                                                                                            .id,
                                                                                        {
                                                                                            ...deal.lead,
                                                                                            dealIds:
                                                                                                stage.deals
                                                                                                    .filter(
                                                                                                        d =>
                                                                                                            d
                                                                                                                .lead
                                                                                                                ?.id ===
                                                                                                            deal
                                                                                                                .lead
                                                                                                                ?.id
                                                                                                    )
                                                                                                    .map(
                                                                                                        d =>
                                                                                                            d.id
                                                                                                    ),
                                                                                            // Não lidas das conversas do lead nesta etapa (sem contar a mesma conversa duas vezes)
                                                                                            unread: Array.from(
                                                                                                new Map(
                                                                                                    stage.deals
                                                                                                        .filter(
                                                                                                            d =>
                                                                                                                d
                                                                                                                    .lead
                                                                                                                    ?.id ===
                                                                                                                    deal
                                                                                                                        .lead
                                                                                                                        ?.id &&
                                                                                                                (
                                                                                                                    d as any
                                                                                                                )
                                                                                                                    .chat
                                                                                                                    ?.id
                                                                                                        )
                                                                                                        .map(
                                                                                                            d => [
                                                                                                                (
                                                                                                                    d as any
                                                                                                                )
                                                                                                                    .chat
                                                                                                                    .id,
                                                                                                                (
                                                                                                                    d as any
                                                                                                                )
                                                                                                                    .chat
                                                                                                                    .unreadCount ??
                                                                                                                    0,
                                                                                                            ]
                                                                                                        )
                                                                                                ).values()
                                                                                            ).reduce(
                                                                                                (
                                                                                                    sum: number,
                                                                                                    n: number
                                                                                                ) =>
                                                                                                    sum +
                                                                                                    n,
                                                                                                0
                                                                                            ),
                                                                                            dealCount:
                                                                                                stage.deals.filter(
                                                                                                    d =>
                                                                                                        d
                                                                                                            .lead
                                                                                                            ?.id ===
                                                                                                        deal
                                                                                                            .lead
                                                                                                            ?.id
                                                                                                )
                                                                                                    .length,
                                                                                            totalValue:
                                                                                                stage.deals
                                                                                                    .filter(
                                                                                                        d =>
                                                                                                            d
                                                                                                                .lead
                                                                                                                ?.id ===
                                                                                                            deal
                                                                                                                .lead
                                                                                                                ?.id
                                                                                                    )
                                                                                                    .reduce(
                                                                                                        (
                                                                                                            sum,
                                                                                                            d
                                                                                                        ) =>
                                                                                                            sum +
                                                                                                            (d.totalValue ||
                                                                                                                0),
                                                                                                        0
                                                                                                    ),
                                                                                            latestDeal:
                                                                                                deal,
                                                                                        }
                                                                                    )
                                                                                }
                                                                            }
                                                                        )
                                                                        const leads =
                                                                            Array.from(
                                                                                leadsMap.values()
                                                                            )

                                                                        if (
                                                                            leads.length ===
                                                                            0
                                                                        ) {
                                                                            return (
                                                                                <div className="flex items-center justify-center h-16 text-xs text-base-content/40 border border-dashed border-base-300 rounded-lg">
                                                                                    Nenhum
                                                                                    lead
                                                                                    nesta
                                                                                    etapa
                                                                                </div>
                                                                            )
                                                                        }

                                                                        return leads.map(
                                                                            lead => (
                                                                                <div
                                                                                    key={
                                                                                        lead.id
                                                                                    }
                                                                                    className={`${lead.unread > 0 ? 'bg-success/10' : 'bg-base-100'} rounded-lg border border-base-300/50 p-3 hover:shadow-md transition-shadow`}
                                                                                >
                                                                                    <div className="flex items-start justify-between mb-1">
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <h4 className="text-sm font-semibold text-base-content line-clamp-1">
                                                                                                <DetailPopupButton
                                                                                                    type="lead"
                                                                                                    id={
                                                                                                        lead.id
                                                                                                    }
                                                                                                    size={
                                                                                                        0
                                                                                                    }
                                                                                                    className="!justify-start !p-0 !h-auto !min-h-0 !font-semibold !text-sm !text-base-content hover:underline !gap-0"
                                                                                                    label={
                                                                                                        lead.name
                                                                                                    }
                                                                                                />
                                                                                            </h4>
                                                                                            {lead.phoneNumber && (
                                                                                                <p className="text-[10px] text-base-content/40">
                                                                                                    {
                                                                                                        lead.phoneNumber
                                                                                                    }
                                                                                                </p>
                                                                                            )}
                                                                                        </div>
                                                                                        <UnreadCountBadge
                                                                                            count={
                                                                                                lead.unread
                                                                                            }
                                                                                        />
                                                                                        {canUpdateDeals &&
                                                                                            lead
                                                                                                .dealIds
                                                                                                .length >
                                                                                                0 && (
                                                                                                <button
                                                                                                    className="btn btn-ghost btn-xs btn-circle flex-shrink-0 ml-1"
                                                                                                    title={
                                                                                                        lead
                                                                                                            .dealIds
                                                                                                            .length >
                                                                                                        1
                                                                                                            ? `Mover as ${lead.dealIds.length} oportunidades deste lead`
                                                                                                            : 'Mover etapa ou funil'
                                                                                                    }
                                                                                                    onClick={e => {
                                                                                                        const rect =
                                                                                                            e.currentTarget.getBoundingClientRect()
                                                                                                        setMoveDropdown(
                                                                                                            prev =>
                                                                                                                prev
                                                                                                                    ?.dealIds[0] ===
                                                                                                                lead
                                                                                                                    .dealIds[0]
                                                                                                                    ? null
                                                                                                                    : {
                                                                                                                          dealIds:
                                                                                                                              lead.dealIds,
                                                                                                                          dealTitle:
                                                                                                                              lead.name,
                                                                                                                          stageId:
                                                                                                                              stage.id,
                                                                                                                          x: rect.right,
                                                                                                                          y:
                                                                                                                              rect.bottom +
                                                                                                                              4,
                                                                                                                      }
                                                                                                        )
                                                                                                    }}
                                                                                                >
                                                                                                    <IoSwapHorizontal
                                                                                                        size={
                                                                                                            14
                                                                                                        }
                                                                                                    />
                                                                                                </button>
                                                                                            )}
                                                                                    </div>

                                                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                                                        {lead.temperature && (
                                                                                            <span className="badge badge-xs badge-outline">
                                                                                                {LEAD_TEMPERATURE_LABELS[
                                                                                                    lead.temperature as LeadTemperature
                                                                                                ] ||
                                                                                                    lead.temperature}
                                                                                            </span>
                                                                                        )}
                                                                                        {lead.currentSentiment && (
                                                                                            <span className="badge badge-xs badge-outline">
                                                                                                {LEAD_SENTIMENT_LABELS[
                                                                                                    lead.currentSentiment as LeadSentimentType
                                                                                                ] ||
                                                                                                    lead.currentSentiment}
                                                                                            </span>
                                                                                        )}
                                                                                        {lead.dealCount >
                                                                                            1 && (
                                                                                            <span className="badge badge-xs badge-ghost">
                                                                                                {
                                                                                                    lead.dealCount
                                                                                                }{' '}
                                                                                                oportunidade(s)
                                                                                            </span>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="flex items-center justify-between mt-2">
                                                                                        <span className="text-xs font-bold text-secondary">
                                                                                            {formatBRL(
                                                                                                lead.totalValue ||
                                                                                                    0
                                                                                            )}
                                                                                        </span>
                                                                                        <div className="flex items-center gap-1">
                                                                                            {(
                                                                                                lead.latestDeal as any
                                                                                            )
                                                                                                ?.chat
                                                                                                ?.id && (
                                                                                                <ChatPopupButton
                                                                                                    chatId={
                                                                                                        (
                                                                                                            lead.latestDeal as any
                                                                                                        )
                                                                                                            .chat
                                                                                                            .id
                                                                                                    }
                                                                                                    chatStatus={
                                                                                                        (
                                                                                                            lead.latestDeal as any
                                                                                                        )
                                                                                                            .chat
                                                                                                            .status
                                                                                                    }
                                                                                                    channel={
                                                                                                        (
                                                                                                            lead.latestDeal as any
                                                                                                        )
                                                                                                            .chat
                                                                                                            .channel
                                                                                                    }
                                                                                                    size={
                                                                                                        14
                                                                                                    }
                                                                                                />
                                                                                            )}
                                                                                            <DetailPopupButton
                                                                                                type="lead"
                                                                                                id={
                                                                                                    lead.id
                                                                                                }
                                                                                                size={
                                                                                                    15
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        )
                                                                    })()}
                                                                </div>
                                                            ) : (
                                                                /* ===== DEALS VIEW ===== */
                                                                <Droppable
                                                                    droppableId={String(
                                                                        stage.id
                                                                    )}
                                                                    type="DEAL"
                                                                >
                                                                    {(
                                                                        dropProvided,
                                                                        dropSnapshot
                                                                    ) => (
                                                                        <div
                                                                            ref={
                                                                                dropProvided.innerRef
                                                                            }
                                                                            {...dropProvided.droppableProps}
                                                                            className={`flex-1 overflow-y-auto p-2 pt-0 space-y-2 min-h-[60px] transition-colors ${
                                                                                dropSnapshot.isDraggingOver
                                                                                    ? 'bg-secondary/5'
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            {stage.total ===
                                                                            0 ? (
                                                                                <div className="flex items-center justify-center h-16 text-xs text-base-content/40 border border-dashed border-base-300 rounded-lg">
                                                                                    Nenhuma
                                                                                    oportunidade
                                                                                    nesta
                                                                                    etapa
                                                                                </div>
                                                                            ) : (
                                                                                stage.deals.map(
                                                                                    (
                                                                                        deal,
                                                                                        dealIndex
                                                                                    ) => (
                                                                                        <Draggable
                                                                                            key={`deal-${deal.id}`}
                                                                                            draggableId={`deal-${deal.id}`}
                                                                                            index={
                                                                                                dealIndex
                                                                                            }
                                                                                            isDragDisabled={
                                                                                                !canUpdateDeals
                                                                                            }
                                                                                        >
                                                                                            {(
                                                                                                dealProvided,
                                                                                                dealSnapshot
                                                                                            ) => {
                                                                                                const daysInStage =
                                                                                                    getDaysInStage(
                                                                                                        deal
                                                                                                    )
                                                                                                const rotting =
                                                                                                    isRotting(
                                                                                                        deal,
                                                                                                        stage
                                                                                                    )

                                                                                                return (
                                                                                                    <div
                                                                                                        ref={
                                                                                                            dealProvided.innerRef
                                                                                                        }
                                                                                                        {...dealProvided.draggableProps}
                                                                                                        className={`deal-card ${((deal as any)?.chat?.unreadCount ?? 0) > 0 ? 'bg-success/10' : 'bg-base-100'} rounded-lg border p-3 hover:shadow-md transition-shadow ${
                                                                                                            dealSnapshot.isDragging
                                                                                                                ? 'shadow-xl rotate-2 border-secondary'
                                                                                                                : rotting
                                                                                                                  ? 'border-warning/50'
                                                                                                                  : 'border-base-300/50'
                                                                                                        }`}
                                                                                                        style={
                                                                                                            dealProvided
                                                                                                                .draggableProps
                                                                                                                .style
                                                                                                        }
                                                                                                    >
                                                                                                        <div className="flex items-start justify-between mb-1">
                                                                                                            <div
                                                                                                                {...dealProvided.dragHandleProps}
                                                                                                                className="flex-1 min-w-0"
                                                                                                            >
                                                                                                                <h4 className="text-sm font-semibold text-base-content line-clamp-1">
                                                                                                                    <DetailPopupButton
                                                                                                                        type="deal"
                                                                                                                        id={
                                                                                                                            deal.id
                                                                                                                        }
                                                                                                                        size={
                                                                                                                            0
                                                                                                                        }
                                                                                                                        className="!justify-start !p-0 !h-auto !min-h-0 !font-semibold !text-sm !text-base-content hover:underline !gap-0"
                                                                                                                        label={
                                                                                                                            deal.title
                                                                                                                        }
                                                                                                                    />
                                                                                                                </h4>
                                                                                                            </div>
                                                                                                            <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                                                                                                                <UnreadCountBadge
                                                                                                                    count={
                                                                                                                        (
                                                                                                                            deal as any
                                                                                                                        )
                                                                                                                            ?.chat
                                                                                                                            ?.unreadCount
                                                                                                                    }
                                                                                                                />
                                                                                                                <button
                                                                                                                    className="btn btn-ghost btn-xs btn-circle"
                                                                                                                    title="Mover etapa ou funil"
                                                                                                                    onClick={e => {
                                                                                                                        const rect =
                                                                                                                            e.currentTarget.getBoundingClientRect()
                                                                                                                        setMoveDropdown(
                                                                                                                            prev =>
                                                                                                                                prev
                                                                                                                                    ?.dealIds[0] ===
                                                                                                                                deal.id
                                                                                                                                    ? null
                                                                                                                                    : {
                                                                                                                                          dealIds:
                                                                                                                                              [
                                                                                                                                                  deal.id,
                                                                                                                                              ],
                                                                                                                                          dealTitle:
                                                                                                                                              deal.title,
                                                                                                                                          stageId:
                                                                                                                                              stage.id,
                                                                                                                                          x: rect.right,
                                                                                                                                          y:
                                                                                                                                              rect.bottom +
                                                                                                                                              4,
                                                                                                                                      }
                                                                                                                        )
                                                                                                                    }}
                                                                                                                >
                                                                                                                    <IoSwapHorizontal
                                                                                                                        size={
                                                                                                                            14
                                                                                                                        }
                                                                                                                    />
                                                                                                                </button>
                                                                                                                {rotting && (
                                                                                                                    <MdWarning
                                                                                                                        className="text-warning text-sm"
                                                                                                                        title={`${daysInStage} dias nesta etapa (limite: ${stage.rottingDays})`}
                                                                                                                    />
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>

                                                                                                        {deal.lead && (
                                                                                                            <div className="flex items-center gap-1.5">
                                                                                                                <p className="text-xs text-base-content/60 truncate">
                                                                                                                    {
                                                                                                                        deal
                                                                                                                            .lead
                                                                                                                            .name
                                                                                                                    }
                                                                                                                </p>
                                                                                                                {(
                                                                                                                    deal as any
                                                                                                                )
                                                                                                                    .currentSentiment && (
                                                                                                                    <span
                                                                                                                        className="badge badge-xs badge-outline"
                                                                                                                        title="Sentimento"
                                                                                                                    >
                                                                                                                        {LEAD_SENTIMENT_LABELS[
                                                                                                                            (
                                                                                                                                deal as any
                                                                                                                            )
                                                                                                                                .currentSentiment as LeadSentimentType
                                                                                                                        ] ||
                                                                                                                            (
                                                                                                                                deal as any
                                                                                                                            )
                                                                                                                                .currentSentiment}
                                                                                                                    </span>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        )}

                                                                                                        {deal.matchedInConversation && (
                                                                                                            <span
                                                                                                                className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium text-info"
                                                                                                                title="O termo buscado aparece nas mensagens da conversa deste contato"
                                                                                                            >
                                                                                                                <MdForum className="text-xs" />
                                                                                                                Encontrado
                                                                                                                na
                                                                                                                conversa
                                                                                                            </span>
                                                                                                        )}

                                                                                                        {/* Última mensagem do cliente/atendente */}
                                                                                                        {(() => {
                                                                                                            const lastMsg =
                                                                                                                ((
                                                                                                                    deal as any
                                                                                                                )
                                                                                                                    ?.chat
                                                                                                                    ?.messages ||
                                                                                                                    [])[0]
                                                                                                            if (
                                                                                                                !lastMsg
                                                                                                            )
                                                                                                                return null
                                                                                                            return (
                                                                                                                <div className="mt-1 mb-1.5 px-2 py-1 bg-base-200/60 rounded text-[10px] text-base-content/50 line-clamp-2">
                                                                                                                    <span className="font-medium">
                                                                                                                        {lastMsg.role ===
                                                                                                                        'user'
                                                                                                                            ? 'Cliente'
                                                                                                                            : 'Atendente'}
                                                                                                                        :
                                                                                                                    </span>{' '}
                                                                                                                    {lastMsg.content?.substring(
                                                                                                                        0,
                                                                                                                        80
                                                                                                                    )}
                                                                                                                    {lastMsg
                                                                                                                        .content
                                                                                                                        ?.length >
                                                                                                                    80
                                                                                                                        ? '...'
                                                                                                                        : ''}
                                                                                                                </div>
                                                                                                            )
                                                                                                        })()}

                                                                                                        <div className="flex items-center justify-between">
                                                                                                            <span className="text-xs font-bold text-secondary">
                                                                                                                {formatBRL(
                                                                                                                    deal.totalValue ||
                                                                                                                        0
                                                                                                                )}
                                                                                                            </span>
                                                                                                            <div className="flex items-center gap-1.5">
                                                                                                                <span
                                                                                                                    className={`text-xs ${rotting ? 'text-warning font-medium' : 'text-base-content/40'}`}
                                                                                                                >
                                                                                                                    {
                                                                                                                        daysInStage
                                                                                                                    }
                                                                                                                    d
                                                                                                                </span>
                                                                                                                {deal.assignedUser ? (
                                                                                                                    <div
                                                                                                                        className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center"
                                                                                                                        title={
                                                                                                                            deal
                                                                                                                                .assignedUser
                                                                                                                                .name
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <span className="text-[9px] font-bold text-secondary">
                                                                                                                            {getUserInitials(
                                                                                                                                deal
                                                                                                                                    .assignedUser
                                                                                                                                    .name
                                                                                                                            )}
                                                                                                                        </span>
                                                                                                                    </div>
                                                                                                                ) : (
                                                                                                                    <MdPerson className="text-xs text-base-content/30" />
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div className="flex items-center justify-end gap-1 mt-1.5 pt-1.5 border-t border-base-200">
                                                                                                            {(
                                                                                                                deal as any
                                                                                                            )
                                                                                                                ?.chat
                                                                                                                ?.id && (
                                                                                                                <ChatPopupButton
                                                                                                                    chatId={
                                                                                                                        (
                                                                                                                            deal as any
                                                                                                                        )
                                                                                                                            .chat
                                                                                                                            .id
                                                                                                                    }
                                                                                                                    chatStatus={
                                                                                                                        (
                                                                                                                            deal as any
                                                                                                                        )
                                                                                                                            .chat
                                                                                                                            .status
                                                                                                                    }
                                                                                                                    channel={
                                                                                                                        (
                                                                                                                            deal as any
                                                                                                                        )
                                                                                                                            .chat
                                                                                                                            .channel
                                                                                                                    }
                                                                                                                    size={
                                                                                                                        14
                                                                                                                    }
                                                                                                                />
                                                                                                            )}
                                                                                                            <DetailPopupButton
                                                                                                                type="deal"
                                                                                                                id={
                                                                                                                    deal.id
                                                                                                                }
                                                                                                            />
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )
                                                                                            }}
                                                                                        </Draggable>
                                                                                    )
                                                                                )
                                                                            )}
                                                                            {
                                                                                dropProvided.placeholder
                                                                            }
                                                                            {stage
                                                                                .deals
                                                                                .length <
                                                                                stage.total && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-ghost btn-xs w-full mt-2"
                                                                                    onClick={() =>
                                                                                        loadMoreForStage(
                                                                                            stage.id
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        stage.loadingMore
                                                                                    }
                                                                                >
                                                                                    {stage.loadingMore
                                                                                        ? 'Carregando...'
                                                                                        : `Carregar mais (${stage.deals.length} de ${stage.total})`}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </Droppable>
                                                            )}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            )
                                        )}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>

                    {showFlowsModal && pipeline && (
                        <PipelineFlowsModal
                            pipeline={pipeline}
                            stages={stagesWithDeals}
                            onClose={() => setShowFlowsModal(false)}
                        />
                    )}

                    {/* Routing Modal — movimentação automática (docs/sales-stage-routing.md) */}
                    {showRoutingModal && pipeline && (
                        <dialog className="modal modal-open z-50">
                            <div className="modal-box !max-w-2xl">
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <h3 className="text-lg font-bold">
                                            Movimentação automática
                                        </h3>
                                        <p className="text-sm text-base-content/60 mt-1">
                                            Para qual etapa o card vai em cada
                                            evento do sistema.
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn-ghost btn-sm btn-circle"
                                        onClick={() =>
                                            setShowRoutingModal(false)
                                        }
                                    >
                                        <MdClose className="text-lg" />
                                    </button>
                                </div>

                                <div className="alert bg-base-200 border-0 text-sm my-4 items-start">
                                    <MdInfoOutline className="text-lg shrink-0 mt-0.5 text-base-content/60" />
                                    <span className="text-base-content/80">
                                        O card só avança: se já estiver mais
                                        adiante, não volta. Quando a etapa de
                                        destino é de ganho, o negócio é fechado
                                        como ganho.
                                    </span>
                                </div>

                                {(['entry', 'move'] as const).map(kind => {
                                    const stages = [...(pipeline.stages || [])]
                                        .sort(
                                            (a, b) => a.sortOrder - b.sortOrder
                                        )
                                        .filter(stage =>
                                            kind === 'entry'
                                                ? !stage.isWonStage &&
                                                  !stage.isLostStage
                                                : !stage.isLostStage
                                        )
                                    return (
                                        <section
                                            key={kind}
                                            className="mb-6 last:mb-0"
                                        >
                                            <h4 className="text-sm font-semibold text-base-content mb-1">
                                                {kind === 'entry'
                                                    ? 'Onde o card nasce'
                                                    : 'Para onde o card avança'}
                                            </h4>
                                            <p className="text-xs text-base-content/60 mb-4">
                                                {kind === 'entry'
                                                    ? 'Sem escolha, o card nasce na primeira etapa do funil.'
                                                    : 'Sem escolha, o card fica onde está.'}
                                            </p>
                                            <div className="space-y-5">
                                                {PIPELINE_ROUTING_FIELDS.filter(
                                                    cfg => cfg.kind === kind
                                                ).map(cfg => {
                                                    const lockedByAutomation =
                                                        cfg.field ===
                                                            'paidStageId' &&
                                                        routingOwner
                                                    return (
                                                        <div
                                                            key={cfg.field}
                                                            className="form-control w-full"
                                                        >
                                                            <label className="label pb-1.5">
                                                                <span className="label-text font-medium">
                                                                    {cfg.label}
                                                                </span>
                                                            </label>
                                                            <select
                                                                className="select select-bordered w-full"
                                                                value={
                                                                    routingForm[
                                                                        cfg
                                                                            .field
                                                                    ] ?? ''
                                                                }
                                                                disabled={
                                                                    !!lockedByAutomation
                                                                }
                                                                onChange={e =>
                                                                    setRoutingForm(
                                                                        prev => ({
                                                                            ...prev,
                                                                            [cfg.field]:
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                                    ? parseInt(
                                                                                          e
                                                                                              .target
                                                                                              .value
                                                                                      )
                                                                                    : null,
                                                                        })
                                                                    )
                                                                }
                                                            >
                                                                <option value="">
                                                                    {kind ===
                                                                    'entry'
                                                                        ? 'Primeira etapa do funil'
                                                                        : 'Não mover'}
                                                                </option>
                                                                {stages.map(
                                                                    stage => (
                                                                        <option
                                                                            key={
                                                                                stage.id
                                                                            }
                                                                            value={
                                                                                stage.id
                                                                            }
                                                                        >
                                                                            {
                                                                                stage.name
                                                                            }
                                                                            {stage.isWonStage
                                                                                ? ' (fecha como ganho)'
                                                                                : ''}
                                                                        </option>
                                                                    )
                                                                )}
                                                            </select>
                                                            <label className="label pt-1.5 pb-0 whitespace-normal">
                                                                <span className="label-text-alt text-base-content/60 whitespace-normal leading-snug">
                                                                    {cfg.help}
                                                                </span>
                                                            </label>
                                                            {lockedByAutomation && (
                                                                <div className="mt-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-base-content/80">
                                                                    Controlado
                                                                    pelo fluxo{' '}
                                                                    <button
                                                                        type="button"
                                                                        className="link link-hover font-semibold"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/sales/automations/flows/${lockedByAutomation.id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {
                                                                            lockedByAutomation.name
                                                                        }
                                                                    </button>
                                                                    . Enquanto
                                                                    ele estiver
                                                                    ativo, é ele
                                                                    quem move o
                                                                    card quando
                                                                    a cobrança é
                                                                    paga.
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </section>
                                    )
                                })}

                                <div className="modal-action">
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() =>
                                            setShowRoutingModal(false)
                                        }
                                        disabled={savingRouting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="btn btn-secondary gap-2"
                                        onClick={handleSaveRouting}
                                        disabled={savingRouting}
                                    >
                                        {savingRouting ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <MdSave className="text-lg" />
                                        )}
                                        Salvar
                                    </button>
                                </div>
                            </div>
                            <form method="dialog" className="modal-backdrop">
                                <button
                                    onClick={() => setShowRoutingModal(false)}
                                >
                                    close
                                </button>
                            </form>
                        </dialog>
                    )}

                    {/* Stage Modal */}
                    {showStageModal && (
                        <dialog className="modal modal-open z-50">
                            <div className="modal-box !max-w-lg">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-bold">
                                        {editingStage
                                            ? 'Editar Etapa'
                                            : 'Nova Etapa'}
                                    </h3>
                                    <button
                                        className="btn btn-ghost btn-sm btn-circle"
                                        onClick={() => setShowStageModal(false)}
                                    >
                                        <MdClose className="text-lg" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex gap-3">
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Cor
                                                </span>
                                            </label>
                                            <input
                                                type="color"
                                                className="w-12 h-12 rounded-lg cursor-pointer border border-base-300"
                                                value={stageColor}
                                                onChange={e =>
                                                    setStageColor(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="form-control flex-1">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Nome *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder="Ex: Proposta Enviada"
                                                value={stageName}
                                                onChange={e =>
                                                    setStageName(e.target.value)
                                                }
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Probabilidade (%)
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered w-full"
                                                min={0}
                                                max={100}
                                                value={stageProbability}
                                                onChange={e =>
                                                    setStageProbability(
                                                        parseInt(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Dias para estagnação
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered w-full"
                                                min={1}
                                                placeholder="Sem limite"
                                                value={stageRottingDays}
                                                onChange={e =>
                                                    setStageRottingDays(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-6">
                                        <label className="label cursor-pointer justify-start gap-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-success checkbox-sm"
                                                checked={stageIsWon}
                                                onChange={e => {
                                                    setStageIsWon(
                                                        e.target.checked
                                                    )
                                                    if (e.target.checked)
                                                        setStageIsLost(false)
                                                }}
                                            />
                                            <span className="label-text">
                                                Etapa de ganho
                                            </span>
                                        </label>
                                        <label className="label cursor-pointer justify-start gap-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-error checkbox-sm"
                                                checked={stageIsLost}
                                                onChange={e => {
                                                    setStageIsLost(
                                                        e.target.checked
                                                    )
                                                    if (e.target.checked)
                                                        setStageIsWon(false)
                                                }}
                                            />
                                            <span className="label-text">
                                                Etapa de perda
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <div className="mt-4 rounded-lg border border-base-300 p-3">
                                    <label className="flex cursor-pointer items-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary toggle-sm mt-0.5"
                                            checked={stageHiddenFromAi}
                                            onChange={e =>
                                                setStageHiddenFromAi(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <span className="flex flex-col gap-1">
                                            <span className="label-text font-medium">
                                                Oculta para a IA
                                            </span>
                                            <span className="text-xs text-base-content/60 leading-relaxed">
                                                A IA não vê esta etapa e não
                                                move oportunidades para ela nem
                                                para fora dela.
                                            </span>
                                        </span>
                                    </label>
                                </div>

                                <div className="modal-action">
                                    <button
                                        className="btn btn-ghost"
                                        onClick={() => setShowStageModal(false)}
                                        disabled={savingStage}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="btn btn-secondary gap-2"
                                        onClick={handleSaveStage}
                                        disabled={
                                            savingStage || !stageName.trim()
                                        }
                                    >
                                        {savingStage ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <MdSave className="text-lg" />
                                        )}
                                        {editingStage ? 'Salvar' : 'Criar'}
                                    </button>
                                </div>
                            </div>
                            <form method="dialog" className="modal-backdrop">
                                <button
                                    onClick={() => setShowStageModal(false)}
                                >
                                    close
                                </button>
                            </form>
                        </dialog>
                    )}

                    {/* Delete Stage Confirmation */}
                    <ConfirmModal
                        isOpen={showDeleteStageModal}
                        onClose={() => {
                            setShowDeleteStageModal(false)
                            setStageToDelete(null)
                        }}
                        onConfirm={confirmDeleteStage}
                        title="Remover etapa"
                        message={`Tem certeza de que deseja remover a etapa "${stageToDelete?.name}"? Esta ação não pode ser desfeita.`}
                        confirmButtonText="Remover"
                        cancelButtonText="Cancelar"
                        loading={deletingStage}
                    />
                </div>

                {movePipelineDeal && pipeline && (
                    <MoveDealPipelineModal
                        dealIds={movePipelineDeal.ids}
                        dealTitle={movePipelineDeal.title}
                        currentPipelineId={pipeline.id}
                        onClose={() => setMovePipelineDeal(null)}
                        onMoved={() => {
                            setMovePipelineDeal(null)
                            loadData()
                        }}
                    />
                )}

                {/* Dropdown fixo para mover deal de etapa */}
                {moveDropdown && (
                    <>
                        <div
                            className="fixed inset-0 z-[200]"
                            onClick={() => setMoveDropdown(null)}
                        />
                        <div
                            className="fixed z-[201] bg-base-100 rounded-lg shadow-xl border border-base-300 py-1 min-w-[200px] flex flex-col"
                            style={{
                                top: moveDropdown.y,
                                left: moveDropdown.x,
                                transform: 'translateX(-100%)',
                                // funil com muitas etapas não pode passar do rodapé da tela
                                maxHeight: `calc(100vh - ${moveDropdown.y}px - 16px)`,
                            }}
                        >
                            {canUpdateDeals && (
                                <button
                                    className="w-full flex gap-2 items-center text-left px-3 py-2 text-sm font-medium hover:bg-base-200 transition-colors border-b border-base-200"
                                    onClick={() => {
                                        setMovePipelineDeal({
                                            ids: moveDropdown.dealIds,
                                            title: moveDropdown.dealTitle,
                                        })
                                        setMoveDropdown(null)
                                    }}
                                >
                                    <MdDriveFileMove /> Mover para outro funil
                                </button>
                            )}
                            <div className="px-3 py-1.5 text-xs font-semibold opacity-50 border-b border-base-200">
                                Mover para etapa
                            </div>
                            <div className="overflow-y-auto">
                                {stagesWithDeals.map(s => (
                                    <button
                                        key={s.id}
                                        className={`w-full flex gap-2 items-center text-left px-3 py-2 text-sm hover:bg-base-200 transition-colors ${s.id === moveDropdown.stageId ? 'bg-secondary/10 font-semibold text-secondary' : ''}`}
                                        onClick={async () => {
                                            if (s.id === moveDropdown.stageId) {
                                                setMoveDropdown(null)
                                                return
                                            }
                                            try {
                                                const results =
                                                    await Promise.all(
                                                        moveDropdown.dealIds.map(
                                                            dealId =>
                                                                moveDealStage(
                                                                    dealId,
                                                                    {
                                                                        stageId:
                                                                            s.id,
                                                                    }
                                                                )
                                                        )
                                                    )
                                                const failed = results.find(
                                                    r => !r.success
                                                )
                                                if (failed) {
                                                    toast.error(
                                                        failed.error ||
                                                            'Erro ao mover oportunidade'
                                                    )
                                                    return
                                                }
                                                setMoveDropdown(null)
                                                loadData()
                                            } catch {
                                                toast.error(
                                                    'Erro ao mover oportunidade'
                                                )
                                            }
                                        }}
                                    >
                                        <BiChevronRight /> {s.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </DetailPopupProvider>
        </ChatPopupProvider>
    )
}

/** Contador de mensagens não lidas da conversa, como o do WhatsApp. */
function UnreadCountBadge({ count }: { count?: number | null }) {
    if (!count || count <= 0) return null
    return (
        <span
            className="min-w-[18px] h-[18px] px-1 rounded-full bg-success text-success-content text-[10px] font-bold flex items-center justify-center flex-shrink-0"
            title={`${count} ${count === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`}
        >
            {count > 99 ? '99+' : count}
        </span>
    )
}
